import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Icon,
  Badge,
  Button,
  Step,
  StepDescription,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  useSteps,
  Avatar,
  Flex,
} from "@chakra-ui/react";
import { FaHeart, FaHandshake, FaLightbulb, FaRocket, FaShieldAlt, FaUserCheck } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const SAND = "#C2A878";
const SAND_LIGHT = "#F5F0E8";

const FinanceServicesBadCredit: React.FC = () => {
  const steps = [
    { title: 'Apply Online', description: 'Quick 5-minute application' },
    { title: 'Assessment', description: 'We review your situation' },
    { title: 'Approval', description: 'Get your decision fast' },
    { title: 'Settlement', description: 'Funds released to you' },
  ];

  const { activeStep } = useSteps({
    index: 0,
    count: steps.length,
  });

  const testimonials = [
    {
      name: "Sarah M.",
      location: "Melbourne, VIC",
      text: "I thought my credit history would stop me from getting a home loan, but the team found a solution that worked. Now I'm a homeowner!",
      rating: 5,
    },
    {
      name: "James T.",
      location: "Brisbane, QLD",
      text: "After a business failure left my credit damaged, I didn't think anyone would help. These guys saw past the numbers and helped me refinance.",
      rating: 5,
    },
    {
      name: "Linda K.",
      location: "Sydney, NSW",
      text: "Bankruptcy three years ago seemed like the end. But with patient guidance, I secured a car loan and am rebuilding my financial future.",
      rating: 5,
    },
  ];

  const howWeHelp = [
    {
      icon: FaUserCheck,
      title: "Individual Assessment",
      description: "We look at your full story, not just a credit score. Every situation is unique.",
    },
    {
      icon: FaLightbulb,
      title: "Expert Solutions",
      description: "Access to specialist bad credit lenders with flexible criteria and understanding.",
    },
    {
      icon: FaHandshake,
      title: "No Judgment Zone",
      description: "Life happens. We're here to help you move forward without judgment or stigma.",
    },
    {
      icon: FaShieldAlt,
      title: "Credit Repair Advice",
      description: "Get practical tips on improving your credit score while securing the finance you need.",
    },
    {
      icon: FaRocket,
      title: "Fast Approvals",
      description: "Bad credit doesn't mean slow service. We work quickly to get you answers.",
    },
    {
      icon: FaHeart,
      title: "Ongoing Support",
      description: "We're with you beyond approval - supporting your journey to financial recovery.",
    },
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/client/finance/services/bad-credit"
        fallbackTitle="Bad Credit Loans - Second Chance Finance Solutions"
        fallbackDescription="Credit challenges shouldn't stop your dreams. Specialist bad credit loans for home, car, and personal finance. Compassionate service, real solutions."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} align="center" textAlign="center">
            <Badge bg={SAND} color={NAVY} fontSize="md" px={4} py={2} borderRadius="full">
              Second Chance Finance
            </Badge>
            <Heading as="h1" size="2xl" fontWeight="bold">
              Bad Credit Doesn't Mean No Credit
            </Heading>
            <Text fontSize="xl" maxW="3xl" opacity={0.95}>
              Everyone deserves a second chance. We specialize in helping Australians with credit challenges
              access the finance they need to move forward with confidence.
            </Text>
            <HStack spacing={4} pt={4}>
              <Button
                size="lg"
                bg={SAND}
                color={NAVY}
                _hover={{ opacity: 0.9 }}
              >
                Check Your Options
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
              >
                Speak to a Specialist
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* How We Help Grid */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="xl" color="gray.800">
                How We Help You Move Forward
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Our specialist team understands that credit problems can happen to anyone.
                We focus on solutions, not excuses.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8} w="full">
              {howWeHelp.map((item, index) => (
                <Box
                  key={index}
                  bg="white"
                  p={8}
                  borderRadius="xl"
                  shadow="md"
                  border="2px solid"
                  borderColor="gray.100"
                  transition="all 0.3s"
                  _hover={{
                    transform: "translateY(-4px)",
                    shadow: "xl",
                    borderColor: SAND,
                  }}
                >
                  <VStack align="start" spacing={4}>
                    <Icon as={item.icon} boxSize={12} color={SAND} />
                    <Heading as="h3" size="md" color="gray.800">
                      {item.title}
                    </Heading>
                    <Text color="gray.600">{item.description}</Text>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Testimonials */}
      <Box py={20} bg="gray.50">
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="xl" color="gray.800">
                Real Stories, Real Success
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                These are just a few of the thousands of Australians we've helped
                overcome credit challenges and achieve their financial goals.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
              {testimonials.map((testimonial, index) => (
                <Box
                  key={index}
                  bg="white"
                  p={8}
                  borderRadius="xl"
                  shadow="lg"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <VStack align="start" spacing={4}>
                    <HStack spacing={1}>
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Text key={i} color="yellow.400" fontSize="xl">★</Text>
                      ))}
                    </HStack>
                    <Text color="gray.700" fontSize="md" fontStyle="italic">
                      "{testimonial.text}"
                    </Text>
                    <Flex align="center" pt={4} borderTop="1px solid" borderColor="gray.200" w="full">
                      <Avatar size="sm" name={testimonial.name} bg={NAVY} mr={3} />
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="bold" fontSize="sm">{testimonial.name}</Text>
                        <Text fontSize="xs" color="gray.500">{testimonial.location}</Text>
                      </VStack>
                    </Flex>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Process Steps */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="xl" color="gray.800">
                Your Journey to Approval
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                We've streamlined the process to make it as clear and straightforward as possible.
              </Text>
            </VStack>

            <Box w="full" maxW="4xl" mx="auto">
              <Stepper index={activeStep} colorScheme="gray" size="lg">
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepIndicator>
                      <StepStatus
                        complete={<StepIcon />}
                        incomplete={<StepNumber />}
                        active={<StepNumber />}
                      />
                    </StepIndicator>

                    <Box flexShrink="0">
                      <StepTitle>{step.title}</StepTitle>
                      <StepDescription>{step.description}</StepDescription>
                    </Box>

                    <StepSeparator />
                  </Step>
                ))}
              </Stepper>
            </Box>

            <VStack spacing={4} pt={8} textAlign="center">
              <Text color="gray.600" maxW="2xl">
                Most applications are assessed within 48 hours. We understand urgency and work
                quickly to provide you with options tailored to your situation.
              </Text>
              <Button
                size="lg"
                bg={SAND}
                color={NAVY}
                _hover={{ transform: "translateY(-2px)", shadow: "lg" }}
              >
                Start Your Application
              </Button>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* What We Accept Section */}
      <Box py={20} bg={SAND_LIGHT}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            <VStack align="start" spacing={6}>
              <Badge bg={SAND} color={NAVY} fontSize="sm" px={3} py={1} borderRadius="full">
                We Consider All Situations
              </Badge>
              <Heading as="h2" size="xl" color="gray.800">
                Credit Issues We Can Help With
              </Heading>
              <Text fontSize="lg" color="gray.600">
                We've successfully helped clients with a wide range of credit challenges:
              </Text>
              <SimpleGrid columns={2} spacing={4} w="full">
                <VStack align="start" spacing={2}>
                  <Text>✓ Defaults</Text>
                  <Text>✓ Bankruptcy (discharged)</Text>
                  <Text>✓ Part IX / Part X</Text>
                  <Text>✓ Judgments</Text>
                </VStack>
                <VStack align="start" spacing={2}>
                  <Text>✓ Missed payments</Text>
                  <Text>✓ High credit utilization</Text>
                  <Text>✓ Low credit score</Text>
                  <Text>✓ Multiple enquiries</Text>
                </VStack>
              </SimpleGrid>
            </VStack>

            <Box bg={NAVY} p={10} borderRadius="2xl" color="white">
              <VStack spacing={6} align="start">
                <Heading as="h3" size="lg">
                  Ready for Your Second Chance?
                </Heading>
                <Text opacity={0.9}>
                  Don't let past mistakes hold you back from your future. Our specialist
                  team is ready to help you find a way forward.
                </Text>
                <VStack align="stretch" spacing={3} w="full" pt={4}>
                  <HStack>
                    <Icon as={FaUserCheck} boxSize={6} />
                    <Text>Compassionate, non-judgmental service</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaLightbulb} boxSize={6} />
                    <Text>Access to specialist bad credit lenders</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaRocket} boxSize={6} />
                    <Text>Fast assessment and approval process</Text>
                  </HStack>
                  <HStack>
                    <Icon as={FaShieldAlt} boxSize={6} />
                    <Text>Confidential and secure application</Text>
                  </HStack>
                </VStack>
                <Button
                  size="lg"
                  bg={SAND}
                  color={NAVY}
                  w="full"
                  _hover={{ opacity: 0.9 }}
                  mt={4}
                >
                  Check My Options
                </Button>
                <Text fontSize="sm" opacity={0.8} textAlign="center" w="full">
                  Free consultation - no obligation
                </Text>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={20} bg={NAVY} color="white">
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading as="h2" size="xl">
              Everyone Deserves a Second Chance
            </Heading>
            <Text fontSize="lg" maxW="2xl" opacity={0.9}>
              Let us help you move past credit challenges and into a brighter financial future.
              Call us today for a confidential, no-obligation consultation.
            </Text>
            <HStack spacing={4}>
              <Button
                size="lg"
                bg={SAND}
                color={NAVY}
                _hover={{ transform: "translateY(-2px)", shadow: "xl" }}
              >
                Apply Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
              >
                Call: 1300 XXX XXX
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesBadCredit;
